import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
  Paper,
  Alert,
  InputAdornment
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  DeleteOutline as DeleteOutlineIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import WorkflowStatusCard from '../common/WorkflowStatusCard';
import AuditTrail from '../AuditTrail';
import ItineraryDownload from '../ItineraryDownload';
import PassengerManagement from '../common/PassengerManagement';
import { ROLES } from '../../constants/roles';
import axios from 'axios';
import {
  getSectorScheduleMs,
  hoursBetweenMs,
  getCharterScheduleSpanHours,
  isLongerThanHours,
  formatDurationHours,
  LONG_CHARTER_HOURS
} from '../sales/SalesEnquiryUtils';

// Helper function to format date/time
const formatDateTime = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

const UPCOMING_SOURCE_LABELS = {
  WORK_ORDER: 'Work Order',
  COMPONENT_INSPECTION: 'Inspection',
  COMPONENT_SERVICE: 'Service',
  ASSEMBLY_TIME_SINCE: 'Assembly TSR',
  COMPONENT_TIME_SINCE: 'Component TSR',
  AMP_SCHEDULE: 'AMP Schedule',
  ADSB_SERVICE_BULLETIN: 'AD/SB'
};

const getUpcomingSourceLabel = (source) => UPCOMING_SOURCE_LABELS[source] || source || '—';

const formatUpcomingDue = (item) => {
  const due = item?.effectiveDueDate || item?.dueDate;
  if (due) {
    const d = new Date(due);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
  if (item?.dueAtLimit?.value != null) {
    return `${item.dueAtLimit.value} ${item.dueAtLimit.unit || ''}`.trim();
  }
  if (item?.dueByRemaining?.value != null) {
    return `Remaining: ${item.dueByRemaining.value} ${item.dueByRemaining.unit || ''}`.trim();
  }
  return 'Not set';
};

const UPCOMING_ASSIGN_FETCH_PARAMS = {
  pastDays: 90,
  useDays: '1',
  days: 365,
  useHours: '1',
  hours: 500,
  useCycles: '1',
  cycles: 5000,
  useLandings: '1',
  landings: 2500
};

const formatMaintenanceInstant = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatMaintenanceWoLabel = (item) => {
  if (!item) return 'Maintenance';
  const wo = item.workOrderNumber ? `WO ${item.workOrderNumber}` : null;
  return wo ? `${wo} — ${item.title || 'Untitled'}` : (item.title || 'Untitled');
};

// Helper function to format actor name
const formatActorName = (actor) => {
  if (!actor) return 'System';
  if (typeof actor === 'string') return 'Unknown User';
  const name = `${actor.firstName || ''} ${actor.lastName || ''}`.trim();
  return name || actor.email || 'Unknown User';
};

// Helper function to format route with validation
export const formatRoute = (sector) => {
  const depAirport = sector.departure?.airport || 'N/A';
  let arrAirport = sector.arrival?.airport || 'N/A';
  
  // Validate arrival airport - check if it looks like a time (contains ":" and matches time pattern)
  if (arrAirport && /^\d{1,2}:\d{2}$/.test(arrAirport.trim())) {
    // This is likely a time value incorrectly stored in arrival.airport
    arrAirport = 'N/A (Invalid Data)';
  }
  
  return `${depAirport} → ${arrAirport}`;
};

export const formatRequirementsSummary = (sector) => {
  const req = sector?.requirements;
  if (!req && !sector?.weatherMediaId) return '—';
  const parts = [];
  if (req?.fuelKg != null && req.fuelKg !== '') parts.push(`${req.fuelKg} kg`);
  if (req?.fuelLitres != null && req.fuelLitres !== '') parts.push(`${req.fuelLitres} L`);
  if (req?.altitude != null && req.altitude !== '') parts.push(`${req.altitude} ft`);
  if (req?.route) parts.push(req.route);
  if (parts.length > 0) return parts.join(' · ');
  if (req?.weather || sector?.weatherMediaId) return 'Weather attached';
  return '—';
};

const normalizePilotIds = (pilots) => [...new Set(
  (pilots || [])
    .map((p) => String(p && (p._id || p)))
    .filter((id) => id && id !== 'undefined' && id !== 'null')
)].sort();

/**
 * BA upload is required on the first sector; later sectors only when pilots changed.
 */
export const sectorRequiresBaUpload = (sector, tripSectors = []) => {
  if (!sector) return true;
  const ordered = [...(tripSectors || [])].sort((a, b) => {
    const seqDiff = (Number(a.seqNo) || 0) - (Number(b.seqNo) || 0);
    if (seqDiff !== 0) return seqDiff;
    return String(a._id || '').localeCompare(String(b._id || ''));
  });
  const idx = ordered.findIndex((s) => String(s._id) === String(sector._id));
  if (idx <= 0) return true;
  const current = normalizePilotIds(sector.assignedPilots);
  const previous = normalizePilotIds(ordered[idx - 1].assignedPilots);
  if (current.length !== previous.length) return true;
  return current.some((id, i) => id !== previous[i]);
};

// Helper function to get status color
export const getStatusColor = (status) => {
  const colors = {
    DRAFT: 'default',
    PENDING_OPS: 'warning',
    CREW_ASSIGNMENT: 'info',
    PLANNED: 'default',
    ASSIGNED: 'info',
    CREW_ACCEPTED: 'success',
    CREW_REJECTED: 'error',
    READY_FOR_BA: 'warning',
    READY_FOR_ATC: 'secondary',
    CLEARED: 'secondary',
    COMPLETED: 'success',
    CHARTER_COMPLETED: 'success',
    SECTOR_COMPLETED: 'success',
    CANCELLED: 'error',
    BA_FAILED: 'error'
  };
  return colors[status] || 'default';
};

// Helper function to render remarks
export const renderRemarks = (sector) => {
  const remarks = [];
  if (sector.remarks?.assignment) remarks.push(`Assignment: ${sector.remarks.assignment}`);
  if (sector.remarks?.requirements) remarks.push(`Requirements: ${sector.remarks.requirements}`);
  if (sector.remarks?.clearance || sector.clearanceRemarks) {
    remarks.push(`Clearance: ${sector.remarks?.clearance || sector.clearanceRemarks}`);
  }
  return remarks.length ? remarks : ['—'];
};

// Helper function to get aircraft name
export const getAircraftName = (aircraftRef, aircraft = []) => {
  if (!aircraftRef) return 'Not assigned';
  const id = typeof aircraftRef === 'string' ? aircraftRef : aircraftRef._id;
  const ac = aircraft.find(a => a._id === id);
  if (ac) {
    const makeModel = [ac.manufacturer, ac.model].filter(Boolean).join(' ');
    const reg = ac.registration || ac.tailNumber || ac.name || 'Aircraft';
    return `${reg}${makeModel ? ` (${makeModel})` : ''}`;
  }
  if (typeof aircraftRef === 'object') {
    const makeModel = [aircraftRef.manufacturer, aircraftRef.model].filter(Boolean).join(' ');
    const reg = aircraftRef.registration || aircraftRef.tailNumber || aircraftRef.name || aircraftRef._id;
    return reg ? `${reg}${makeModel ? ` (${makeModel})` : ''}` : 'Not assigned';
  }
  return 'Not assigned';
};

// Helper function to resolve pilot ID
export const resolvePilotId = (pilotRef) => {
  if (!pilotRef) return null;
  if (typeof pilotRef === 'string') return pilotRef;
  if (typeof pilotRef === 'object') return pilotRef._id || pilotRef;
  return pilotRef;
};

// Helper function to get pilot display name
export const getPilotDisplayName = (pilotRef, pilots = []) => {
  const id = resolvePilotId(pilotRef);
  if (!id) return 'Unknown';
  const pilot = pilots.find(p => p._id === id);
  if (!pilot) return id;
  const name = `${pilot.firstName || ''} ${pilot.lastName || ''}`.trim();
  return name || pilot.email || id;
};

// Helper function to get pilot names
export const getPilotNames = (pilotRefs = [], pilots = []) => {
  if (!pilotRefs.length) return 'Unassigned';
  return pilotRefs
    .map((ref) => {
      const id = resolvePilotId(ref);
      if (!id) return 'Unknown';
      const pilot = pilots.find(p => p._id === id);
      if (pilot) {
        const name = `${pilot.firstName || ''} ${pilot.lastName || ''}`.trim();
        return name || pilot.email || 'Unknown';
      }
      if (typeof ref === 'object') {
        const name = `${ref.firstName || ''} ${ref.lastName || ''}`.trim();
        return name || ref.email || id || 'Unknown';
      }
      return 'Unknown';
    })
    .join(', ');
};

// Helper function to render crew response chips
export const renderCrewResponseChips = (sector, pilots = []) => {
  const total = sector.assignedPilots?.length || 0;
  const wrapChip = (title, chip) => (
    <Tooltip title={title || ''} arrow>
      <span>{chip}</span>
    </Tooltip>
  );
  if (!total) {
    return wrapChip(
      'No crew assigned',
      <Chip
        label="No crew assigned"
        size="small"
        variant="outlined"
      />
    );
  }
  const responses = Array.isArray(sector.crewResponses) ? sector.crewResponses : [];
  const latestByPilot = responses.reduce((acc, resp) => {
    const pilotId = resolvePilotId(resp.pilotId);
    if (!pilotId) return acc;
    const existing = acc.get(pilotId);
    if (!existing || new Date(resp.respondedAt || 0) > new Date(existing.respondedAt || 0)) {
      acc.set(pilotId, resp);
    }
    return acc;
  }, new Map());
  const respondedCount = latestByPilot.size;
  const acceptedResponses = Array.from(latestByPilot.values()).filter(r => r.decision === 'ACCEPT');
  const acceptedCount = acceptedResponses.length;
  const pendingCount = Math.max(total - respondedCount, 0);
  const rejection = Array.from(latestByPilot.values()).find(r => r.decision === 'REJECT');
  const acceptedNames = acceptedResponses.map(r => getPilotDisplayName(r.pilotId, pilots));
  const pendingNames = (sector.assignedPilots || [])
    .map(resolvePilotId)
    .filter(id => id && !latestByPilot.has(id))
    .map(id => getPilotDisplayName(id, pilots));
  const rejectionName = rejection ? getPilotDisplayName(rejection.pilotId, pilots) : null;
  const rejectionNote = rejection?.remarks ? ` – ${rejection.remarks}` : '';

  return (
    <Stack spacing={0.5}>
      {wrapChip(
        acceptedNames.length ? `Accepted: ${acceptedNames.join(', ')}` : 'No acceptances yet',
        <Chip
          label={`Responses: ${acceptedCount}/${total}`}
          size="small"
          color={rejection ? 'error' : acceptedCount === total ? 'success' : 'warning'}
          variant={acceptedCount === total && !rejection ? 'outlined' : 'filled'}
        />
      )}
      {pendingCount > 0 && !rejection && wrapChip(
        pendingNames.length ? `Pending: ${pendingNames.join(', ')}` : 'Awaiting crew responses',
        <Chip
          label={`Pending: ${pendingCount}`}
          size="small"
          color="warning"
          variant="outlined"
        />
      )}
      {rejection && wrapChip(
        `Rejected by ${rejectionName || 'Unknown'}${rejectionNote}`,
        <Chip
          label={`Rejected: ${rejectionName || 'Unknown'}`}
          size="small"
          color="error"
          variant="outlined"
        />
      )}
    </Stack>
  );
};

// Assignment Dialog Component
export const AssignmentDialog = ({
  open,
  onClose,
  selectedSector,
  selectedTrip,
  sectors = [],
  user,
  assignmentForm,
  setAssignmentForm,
  aircraft,
  pilots,
  assigning,
  assignSector,
  getStatusColor
}) => {
  const [upcomingItems, setUpcomingItems] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [upcomingError, setUpcomingError] = useState('');
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [pilotSearch, setPilotSearch] = useState('');

  useEffect(() => {
    if (!open) {
      setPilotSearch('');
    }
  }, [open]);

  const activePilots = useMemo(
    () => (pilots || []).filter((pilot) => pilot.isPilotActive && pilot.isActive !== false),
    [pilots]
  );

  const filteredPilots = useMemo(() => {
    const q = pilotSearch.trim().toLowerCase();
    if (!q) return activePilots;
    return activePilots.filter((pilot) => {
      const name = `${pilot.firstName || ''} ${pilot.lastName || ''}`.trim().toLowerCase();
      const email = String(pilot.email || '').toLowerCase();
      const license = String(pilot.licenseNumber || '').toLowerCase();
      const phone = String(pilot.phone || '').toLowerCase();
      return (
        name.includes(q)
        || email.includes(q)
        || license.includes(q)
        || phone.includes(q)
      );
    });
  }, [activePilots, pilotSearch]);

  useEffect(() => {
    if (!open || !assignmentForm.aircraftId) {
      setUpcomingItems([]);
      setUpcomingError('');
      setLoadingUpcoming(false);
      setScheduleInfo(null);
      setScheduleError('');
      setLoadingSchedule(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingUpcoming(true);
    setUpcomingError('');
    setUpcomingItems([]);
    setLoadingSchedule(true);
    setScheduleError('');
    setScheduleInfo(null);

    axios
      .get('/api/maintenance/upcoming', {
        params: {
          ...UPCOMING_ASSIGN_FETCH_PARAMS,
          aircraftId: String(assignmentForm.aircraftId)
        }
      })
      .then((res) => {
        if (cancelled) return;
        setUpcomingItems((res.data?.items || []).slice(0, 5));
      })
      .catch((err) => {
        if (cancelled) return;
        setUpcomingItems([]);
        setUpcomingError(
          err.response?.status === 403
            ? 'You do not have permission to view upcoming maintenance.'
            : 'Failed to load upcoming maintenance.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingUpcoming(false);
      });

    if (selectedSector?._id) {
      axios
        .get(`/api/ops/sectors/${selectedSector._id}/aircraft-maintenance`, {
          params: { aircraftId: String(assignmentForm.aircraftId) }
        })
        .then((res) => {
          if (cancelled) return;
          setScheduleInfo(res.data || null);
        })
        .catch((err) => {
          if (cancelled) return;
          setScheduleInfo(null);
          setScheduleError(
            err.response?.data?.message || 'Failed to load maintenance schedule for this sector.'
          );
        })
        .finally(() => {
          if (!cancelled) setLoadingSchedule(false);
        });
    } else {
      setLoadingSchedule(false);
    }

    return () => {
      cancelled = true;
    };
  }, [open, assignmentForm.aircraftId, selectedSector?._id]);

  const sectorMs = getSectorScheduleMs(selectedSector);
  const sectorHours = hoursBetweenMs(sectorMs.start, sectorMs.end);
  const charterHours = getCharterScheduleSpanHours(sectors?.length ? sectors : (selectedSector ? [selectedSector] : []));
  const showLongDutyWarning = isLongerThanHours(sectorHours) || isLongerThanHours(charterHours);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Aircraft & Pilots</DialogTitle>
      <DialogContent>
        {showLongDutyWarning && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {isLongerThanHours(sectorHours) && isLongerThanHours(charterHours)
              ? `This sector is ${formatDurationHours(sectorHours)} and the charter spans ${formatDurationHours(charterHours)} from first departure to last arrival (over ${LONG_CHARTER_HOURS} hours). Warning only — you can still assign.`
              : isLongerThanHours(sectorHours)
                ? `This sector duty window is ${formatDurationHours(sectorHours)} (over ${LONG_CHARTER_HOURS} hours). Warning only — you can still assign.`
                : `This charter spans ${formatDurationHours(charterHours)} from first departure to last arrival (over ${LONG_CHARTER_HOURS} hours). Warning only — you can still assign.`}
          </Alert>
        )}
        {/* Workflow Status Card for Sector */}
        {selectedSector && (
          <Box sx={{ mb: 2 }}>
            <WorkflowStatusCard sector={selectedSector} trip={selectedTrip} user={user} />
          </Box>
        )}
        
        {/* Status History Accordion */}
        {selectedSector && selectedSector.statusHistory && selectedSector.statusHistory.length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Status History ({selectedSector.statusHistory.length} entries)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {selectedSector.statusHistory.slice().reverse().map((entry, idx) => (
                  <ListItem key={idx} sx={{ pl: 0, borderLeft: idx < selectedSector.statusHistory.length - 1 ? '2px solid' : 'none', borderColor: 'divider', ml: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: (theme) => {
                        const color = getStatusColor(entry.status);
                        return color === 'success' ? theme.palette.success.main :
                               color === 'error' ? theme.palette.error.main :
                               color === 'warning' ? theme.palette.warning.main :
                               color === 'info' ? theme.palette.info.main :
                               theme.palette.grey[500];
                      }, 
                      width: 32, 
                      height: 32, 
                      mr: 2 
                    }}>
                      {entry.status.charAt(0)}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                          {entry.status.replace(/_/g, ' ')}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            By: {formatActorName(entry.actorId)} • {formatDateTime(entry.changedAt)}
                          </Typography>
                          {entry.remarks && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                              "{entry.remarks}"
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <FormControl fullWidth margin="normal">
          <InputLabel>Aircraft</InputLabel>
          <Select
            value={assignmentForm.aircraftId}
            onChange={(e) => setAssignmentForm(prev => ({ ...prev, aircraftId: e.target.value }))}
          >
            {aircraft.map((ac) => (
              <MenuItem key={ac._id} value={ac._id}>
                {ac.registration} ({ac.manufacturer} {ac.model})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {assignmentForm.aircraftId && (
          <Paper variant="outlined" sx={{ mt: 1, mb: 2, p: 1.5 }}>
            <Typography variant="subtitle2" gutterBottom>
              Maintenance during this sector
            </Typography>
            {loadingSchedule ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : scheduleError ? (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {scheduleError}
              </Typography>
            ) : scheduleInfo?.overlapsSector ? (
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                {(scheduleInfo.overlapping || []).map((item) => (
                  <Alert key={item._id} severity="warning" sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {formatMaintenanceWoLabel(item)} overlaps this sector
                    </Typography>
                    <Typography variant="caption" display="block">
                      Enters maintenance: {formatMaintenanceInstant(item.entersAt)}
                      {item.windowLabel ? ` (${item.windowLabel})` : ''}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Available again: {formatMaintenanceInstant(item.availableAgainAt)}
                    </Typography>
                  </Alert>
                ))}
                {selectedTrip?.flightType !== 'MAINTENANCE' && (
                  <Typography variant="caption" color="error">
                    Assignment will be blocked unless this is a maintenance trip.
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                No scheduled maintenance overlaps this sector&apos;s flight time.
              </Typography>
            )}

            {!loadingSchedule && !scheduleError && scheduleInfo?.nextEntry && (
              <Box sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  Next maintenance entry
                </Typography>
                <Typography variant="body2">
                  {formatMaintenanceWoLabel(scheduleInfo.nextEntry)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Enters: {formatMaintenanceInstant(scheduleInfo.nextEntry.entersAt)}
                  {scheduleInfo.nextEntry.windowLabel ? ` (${scheduleInfo.nextEntry.windowLabel})` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Available again: {formatMaintenanceInstant(scheduleInfo.nextEntry.availableAgainAt)}
                </Typography>
                {scheduleInfo.nextEntry.overlapsSector && (
                  <Chip label="During this sector" size="small" color="warning" sx={{ mt: 0.5 }} />
                )}
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
              Next upcoming maintenance / inspections
            </Typography>
            {loadingUpcoming ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : upcomingError ? (
              <Typography variant="body2" color="error">
                {upcomingError}
              </Typography>
            ) : upcomingItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No upcoming maintenance or inspections found for this aircraft.
              </Typography>
            ) : (
              <List dense disablePadding>
                {upcomingItems.map((item, idx) => (
                  <ListItem
                    key={`${item.source}-${item.sourceId}-${item.tsrIndex ?? ''}-${idx}`}
                    alignItems="flex-start"
                    sx={{
                      px: 0,
                      borderBottom: idx < upcomingItems.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={500}>
                            {item.title || 'Untitled'}
                          </Typography>
                          <Chip label={getUpcomingSourceLabel(item.source)} size="small" variant="outlined" />
                          {item.type && (
                            <Chip label={item.type.replace(/_/g, ' ')} size="small" color="info" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Due: {formatUpcomingDue(item)}
                          {item.priority ? ` · Priority: ${item.priority}` : ''}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Select Pilots (Active Only)
          {assignmentForm.assignedPilots?.length
            ? ` · ${assignmentForm.assignedPilots.length} selected`
            : ''}
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search pilots by name, email, license, or phone…"
          value={pilotSearch}
          onChange={(e) => setPilotSearch(e.target.value)}
          margin="dense"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: pilotSearch ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear pilot search"
                  onClick={() => setPilotSearch('')}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
        <Box
          sx={{
            mt: 1,
            maxHeight: 260,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1.5,
            py: 0.5
          }}
        >
          {filteredPilots.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {activePilots.length === 0
                ? 'No active pilots available.'
                : `No pilots match “${pilotSearch.trim()}”.`}
            </Typography>
          ) : (
            filteredPilots.map((pilot) => (
              <FormControlLabel
                key={pilot._id}
                sx={{ display: 'flex', width: '100%', mr: 0, alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    checked={assignmentForm.assignedPilots.includes(pilot._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setAssignmentForm((prev) => ({
                          ...prev,
                          assignedPilots: [...prev.assignedPilots, pilot._id],
                          crewWeights: { ...prev.crewWeights, [pilot._id]: prev.crewWeights[pilot._id] ?? 70 }
                        }));
                      } else {
                        setAssignmentForm((prev) => {
                          const next = {
                            ...prev,
                            assignedPilots: prev.assignedPilots.filter((id) => id !== pilot._id)
                          };
                          const { [pilot._id]: _, ...rest } = prev.crewWeights || {};
                          next.crewWeights = rest;
                          return next;
                        });
                      }
                    }}
                  />
                }
                label={
                  <Box sx={{ py: 0.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {`${pilot.firstName || ''} ${pilot.lastName || ''}`.trim() || 'Unnamed pilot'}
                      </Typography>
                      {pilot.role === ROLES.LOAN_PILOT && (
                        <Chip size="small" label="Loan" color="secondary" variant="outlined" />
                      )}
                      {pilot.role === ROLES.EXTERNAL_PILOT && (
                        <Chip size="small" label="External" color="info" variant="outlined" />
                      )}
                    </Stack>
                    {(pilot.email || pilot.licenseNumber) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[pilot.email, pilot.licenseNumber].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))
          )}
        </Box>

        {(assignmentForm.assignedPilots?.length > 0) && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Crew weight (kg) per pilot:</Typography>
            {assignmentForm.assignedPilots.map((pilotId) => {
              const pilot = pilots.find(p => p._id === pilotId);
              const label = pilot ? `${pilot.firstName} ${pilot.lastName}` : pilotId;
              return (
                <TextField
                  key={pilotId}
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: 0.1 }}
                  label={label}
                  value={assignmentForm.crewWeights?.[pilotId] ?? 70}
                  onChange={(e) => {
                    const v = e.target.value === '' ? 70 : parseFloat(e.target.value);
                    setAssignmentForm(prev => ({
                      ...prev,
                      crewWeights: { ...prev.crewWeights, [pilotId]: (v !== '' && !isNaN(v)) ? v : 70 }
                    }));
                  }}
                  margin="dense"
                  sx={{ display: 'block', maxWidth: 280 }}
                />
              );
            })}
          </>
        )}

        <TextField
          fullWidth
          label="Remarks (Optional)"
          value={assignmentForm.remarks}
          onChange={(e) => setAssignmentForm(prev => ({ ...prev, remarks: e.target.value }))}
          margin="normal"
          multiline
          rows={3}
          placeholder="Add any remarks about this assignment..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>Cancel</Button>
        <Button onClick={assignSector} variant="contained" disabled={assigning || !assignmentForm.aircraftId || !assignmentForm.assignedPilots?.length}>
          {assigning ? 'Assigning...' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Clearance Dialog Component
export const ClearanceDialog = ({
  open,
  onClose,
  clearanceForm,
  setClearanceForm,
  submitClearanceToCrew
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send ATC Clearance to Crew</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Clearance Number"
          value={clearanceForm.clearanceNumber}
          onChange={(e) => setClearanceForm(prev => ({ ...prev, clearanceNumber: e.target.value }))}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Remarks (Optional)"
          value={clearanceForm.remarks}
          onChange={(e) => setClearanceForm(prev => ({ ...prev, remarks: e.target.value }))}
          margin="normal"
          multiline
          rows={3}
          placeholder="Add any remarks about the clearance..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={submitClearanceToCrew} variant="contained">Send to Crew</Button>
      </DialogActions>
    </Dialog>
  );
};

// Requirements Dialog Component
export const RequirementsDialog = ({
  open,
  onClose,
  requirementsForm,
  setRequirementsForm,
  weatherFile,
  setWeatherFile,
  tripKitFiles,
  setTripKitFiles,
  selectedSector,
  sectors,
  removeTripKitFile,
  updateRequirements,
  user,
  verifyTripKit,
  verifyingTripKit
}) => {
  const currentSector = (selectedSector && sectors && sectors.find((s) => s._id === selectedSector._id)) || selectedSector;
  const existingTripKit = (currentSector?.tripKitMediaIds && currentSector.tripKitMediaIds.length)
    ? currentSector.tripKitMediaIds
    : (currentSector?.tripKitMediaId ? [currentSector.tripKitMediaId] : []);
  const hasTripKit = existingTripKit.length > 0 || (tripKitFiles && tripKitFiles.length > 0);
  const tripKitVerified = !!currentSector?.tripKitVerification?.verified;
  const canVerifyTripKit = user?.role && [
    ROLES.OPERATIONS_MANAGER,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ].includes(user.role);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Flight Requirements</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Planned Fuel Consumption (kg)"
          type="number"
          value={requirementsForm.fuelKg}
          onChange={(e) => setRequirementsForm(prev => ({ ...prev, fuelKg: e.target.value }))}
          margin="normal"
          required
          inputProps={{ min: 0, step: 'any' }}
        />
        <TextField
          fullWidth
          label="Planned Fuel Consumption (L) — optional"
          type="number"
          value={requirementsForm.fuelLitres}
          onChange={(e) => setRequirementsForm(prev => ({ ...prev, fuelLitres: e.target.value }))}
          margin="normal"
          inputProps={{ min: 0, step: 'any' }}
        />
        <TextField
          fullWidth
          label="Altitude (feet)"
          type="number"
          value={requirementsForm.altitude}
          onChange={(e) => setRequirementsForm(prev => ({ ...prev, altitude: e.target.value }))}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Route"
          value={requirementsForm.route}
          onChange={(e) => setRequirementsForm(prev => ({ ...prev, route: e.target.value }))}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Weather"
          value={requirementsForm.weather}
          onChange={(e) => setRequirementsForm(prev => ({ ...prev, weather: e.target.value }))}
          margin="normal"
        />

        <Box sx={{ mt: 1, mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            size="small"
          >
            {weatherFile ? 'Change Weather File' : 'Upload Weather File (Optional)'}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setWeatherFile(file);
              }}
            />
          </Button>
          {weatherFile && (
            <Typography variant="caption" sx={{ ml: 1 }}>
              Selected: {weatherFile.name}
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>TripKit Files</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Any Ops role can upload. Ops Manager reviews and verifies.
        </Typography>
        <Box sx={{ mt: 0.5, mb: 2 }}>
          {existingTripKit.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                size="small"
                label={tripKitVerified ? 'TripKit Verified' : 'Awaiting Verification'}
                color={tripKitVerified ? 'success' : 'warning'}
              />
              {tripKitVerified && currentSector?.tripKitVerification?.verifiedByName && (
                <Typography variant="caption" color="text.secondary">
                  by {currentSector.tripKitVerification.verifiedByName}
                </Typography>
              )}
            </Stack>
          )}
          {existingTripKit.length > 0 && (
            <List dense sx={{ mb: 1 }}>
              {existingTripKit.map((media, idx) => {
                const id = typeof media === 'object' && media._id ? media._id : media;
                const fileUrl = typeof media === 'object' ? media.fileUrl : null;
                const label = fileUrl ? (fileUrl.split('/').pop() || `TripKit ${idx + 1}`) : `TripKit ${idx + 1}`;
                return (
                  <ListItem key={id} dense>
                    <ListItemText
                      primary={
                        fileUrl ? (
                          <Typography
                            component="a"
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                          >
                            {label}
                          </Typography>
                        ) : label
                      }
                      secondary="Uploaded"
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => selectedSector?._id && removeTripKitFile(selectedSector._id, id)}
                        aria-label="Remove TripKit file"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
          {(tripKitFiles || []).length > 0 && (
            <List dense sx={{ mb: 1 }}>
              {(tripKitFiles || []).map((file, idx) => (
                <ListItem key={`new-${idx}-${file.name}`} dense>
                  <ListItemText primary={file.name} secondary="New file" />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => setTripKitFiles((prev) => prev.filter((_, i) => i !== idx))}
                      aria-label="Remove file"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              component="label"
              size="small"
              startIcon={<AttachFileIcon />}
            >
              Add TripKit files
              <input
                type="file"
                hidden
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xls,.xlsx,.csv"
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  setTripKitFiles((prev) => (prev || []).concat(files));
                  e.target.value = '';
                }}
              />
            </Button>
            {canVerifyTripKit && existingTripKit.length > 0 && !tripKitVerified && (
              <Button
                variant="contained"
                color="success"
                size="small"
                disabled={verifyingTripKit}
                onClick={() => selectedSector?._id && verifyTripKit?.(selectedSector._id)}
              >
                {verifyingTripKit ? 'Verifying…' : 'Verify TripKit'}
              </Button>
            )}
          </Stack>
          {!hasTripKit && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              At least one TripKit file is required.
            </Typography>
          )}
        </Box>

        <TextField
          fullWidth
          label="Remarks (Optional)"
          value={requirementsForm.remarks}
          onChange={(e) => setRequirementsForm(prev => ({ ...prev, remarks: e.target.value }))}
          margin="normal"
          multiline
          rows={3}
          placeholder="Add any remarks about these requirements..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={updateRequirements} variant="contained">Update</Button>
      </DialogActions>
    </Dialog>
  );
};

// Audit Trail Dialog Component
export const AuditTrailDialog = ({
  open,
  onClose,
  auditTrailData
}) => {
  return (
    <>
      {open && auditTrailData && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="lg"
          fullWidth
          fullScreen
        >
          <AuditTrail
            entityType={auditTrailData.entityType}
            entityId={auditTrailData.entityId}
            entityName={auditTrailData.entityName}
            onClose={onClose}
          />
        </Dialog>
      )}
    </>
  );
};

// Itinerary Dialog Component
export const ItineraryDialog = ({
  open,
  selectedTripForItinerary,
  onClose
}) => {
  return (
    <>
      {open && selectedTripForItinerary && (
        <ItineraryDownload
          charterNumber={selectedTripForItinerary.charterNumber}
          charterStatus={selectedTripForItinerary.status}
          onClose={onClose}
        />
      )}
    </>
  );
};

// Sector Passenger Dialog Component
export const SectorPassengerDialog = ({
  open,
  onClose,
  selectedSectorForPassengers,
  sectors = [],
  selectedTrip,
  user,
  fetchTripDetail,
  buildErrorMessage,
  showToast
}) => {
  // Use latest sector from sectors when available (after fetchTripDetail)
  const sector = (open && selectedSectorForPassengers && sectors?.length)
    ? (sectors.find(s => s._id === selectedSectorForPassengers._id) || selectedSectorForPassengers)
    : selectedSectorForPassengers;

  const [baggageWeight, setBaggageWeight] = useState(sector?.baggageWeight != null ? sector.baggageWeight : 20);
  useEffect(() => {
    if (sector?.baggageWeight != null) setBaggageWeight(sector.baggageWeight);
    else if (sector) setBaggageWeight(20);
  }, [sector?._id, sector?.baggageWeight]);

  const savePassengersAndBaggage = async (passengers, baggageKg) => {
    if (!sector || !selectedTrip) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/ops/sectors/${sector._id}/passengers`, {
        passengers: passengers || sector.passengers || [],
        baggageWeight: baggageKg != null ? baggageKg : (sector.baggageWeight != null ? sector.baggageWeight : 20)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      const updated = response.data;
      const charterNumber = selectedTrip.charterNumber || 'N/A';
      showToast(`Passengers updated successfully! Charter: ${charterNumber} | Sector ${updated.seqNo} | Passengers: ${(passengers || sector.passengers || []).length} | Total Weight: ${updated.totalWeight ?? 0} kg`, 'success');
      if (passengers !== undefined) onClose();
    } catch (error) {
      console.error('Failed to update sector passengers:', error);
      showToast(buildErrorMessage('Passenger update', error), 'error');
    }
  };

  const handleUpdate = async (passengers, totalWeight) => {
    await savePassengersAndBaggage(passengers, baggageWeight);
  };

  const handleUpdateBaggageOnly = async () => {
    await savePassengersAndBaggage(sector.passengers || [], baggageWeight);
  };

  if (!open || !selectedSectorForPassengers) return null;

  const canEdit = (() => {
    const canOverride = [ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
    const correctionRoles = [
      ROLES.SALES,
      ROLES.OPERATIONS_MANAGER,
      ROLES.OPERATIONS_EXECUTIVE,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.SUPER_ADMIN
    ];
    const completedCorrection = correctionRoles.includes(user?.role) && (
      ['COMPLETED', 'CHARTER_COMPLETED'].includes(selectedTrip?.status)
      || ['COMPLETED', 'SECTOR_COMPLETED'].includes(sector?.status)
    );
    return !['CLEARED', 'ATC_PASSED', 'COMPLETED', 'SECTOR_COMPLETED'].includes(sector?.status)
      || canOverride
      || completedCorrection;
  })();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        Manage Passengers - Sector {sector?.seqNo}
      </DialogTitle>
      <DialogContent>
        <PassengerManagement
          tripId={null}
          passengers={sector?.passengers && sector.passengers.length > 0 
            ? sector.passengers 
            : (selectedTrip?.passengers || [])}
          onUpdate={handleUpdate}
          readOnly={!canEdit}
          optionalBaggageWeight={baggageWeight != null ? baggageWeight : 20}
        />
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 0.1 }}
            label="Baggage Weight (kg) - Optional"
            value={baggageWeight ?? ''}
            onChange={(e) => {
              const v = e.target.value === '' ? '' : parseFloat(e.target.value);
              setBaggageWeight((v !== '' && !isNaN(v)) ? v : 20);
            }}
            margin="normal"
            helperText="Default 20 kg if empty. Included in sector total weight."
            disabled={!canEdit}
          />
          {canEdit && (
            <Button variant="outlined" size="small" onClick={handleUpdateBaggageOnly} sx={{ mt: 1 }}>
              Update sector (save baggage only)
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const MroEngineerAssignDialog = ({
  open,
  onClose,
  selectedTrip,
  mroEngineers,
  mroForm,
  setMroForm,
  assigningMro,
  assignMroEngineer
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>
      Assign MRO Engineer (optional)
      {selectedTrip ? ` — ${selectedTrip.charterNumber || selectedTrip.title}` : ''}
    </DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Optionally assign an MRO engineer to open the Technical Log Book before pilot assignment.
        Clearing the engineer does not delete an already-opened techlog.
      </Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel>MRO Engineer</InputLabel>
        <Select
          label="MRO Engineer"
          value={mroForm.assignedMroEngineerId || ''}
          onChange={(e) => setMroForm((prev) => ({ ...prev, assignedMroEngineerId: e.target.value }))}
        >
          <MenuItem value="">
            <em>None (skip)</em>
          </MenuItem>
          {(mroEngineers || []).map((eng) => (
            <MenuItem key={eng._id} value={eng._id}>
              {`${eng.firstName || ''} ${eng.lastName || ''}`.trim() || eng.email}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        margin="normal"
        label="Remarks (optional)"
        multiline
        minRows={2}
        value={mroForm.remarks || ''}
        onChange={(e) => setMroForm((prev) => ({ ...prev, remarks: e.target.value }))}
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={assigningMro}>Cancel</Button>
      <Button
        variant="contained"
        onClick={assignMroEngineer}
        disabled={assigningMro}
      >
        {assigningMro ? 'Saving…' : 'Save'}
      </Button>
    </DialogActions>
  </Dialog>
);

